import {
  EventLocationFormValues,
  getEventLocationFormValues,
  isEmptyLocation,
} from "../EventCreateEditModal/useEventCreateEditForm";
import { Path, PathValue, useWatch } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { EventCreateEditFormValues } from "../EventCreateEditModal/useEventCreateEditForm";
import { useEffect, useRef, useState } from "react";
import { createMultilingual } from "shared/utils";
import _ from "lodash";

export const useInPerson = () => {
  const { control, getFieldState, getValues, setValue, trigger } =
    useFormContext<EventCreateEditFormValues>();
  const eventLocations = useWatch({ control, name: "eventLocations" });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);

  const [index, setIndex] = useState(0);
  const [formFieldRoot, setFormFieldRoot] =
    useState<Path<EventCreateEditFormValues>>("eventLocations.0");
  const [locationCopy, setLocationCopy] =
    useState<EventLocationFormValues | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");

  /**
   * Handles the click event for adding a new location.
   * - When adding a new location, we need to set an empty copy of the location form values.
   * @param index - The index of the new location.
   */
  const handleAddLocationClick = (index: number) => {
    const copy = getEventLocationFormValues({ venue: createMultilingual("") });
    setLocationCopy(copy);
    setModalMode("add");
    setIndex(index);
    setFormFieldRoot(`eventLocations.${index}`);
    setIsEditModalOpen(true);
    setValue("eventLocations", [...getValues("eventLocations"), copy], {
      shouldDirty: true,
    });
  };

  /**
   * Handles the click event for editing a location.
   * @param index - The index of the location to edit.
   * @param location - The location to edit.
   * @param formFieldRoot - The path to the location in the form.
   */
  const handleEditLocationClick = (
    index: number,
    location: EventLocationFormValues,
    formFieldRoot: Path<EventCreateEditFormValues>,
  ) => {
    setLocationCopy(_.cloneDeep(location));
    setModalMode("edit");
    setIndex(index);
    setFormFieldRoot(formFieldRoot);
    setIsEditModalOpen(true);
  };

  /**
   * Handles the click event for deleting a location.
   * - Show a confirmation modal prior to deleting the location.
   * @param index - The index of the location to delete.
   */
  const handleDeleteLocationClick = (index: number) => {
    setIndex(index);
    setIsDeleteConfirmationModalOpen(true);
  };

  /**
   * Handles the action click event for the delete confirmation modal.
   * - If confirmed, delete the location from the eventLocations array.
   * @param isConfirmed - Whether the user confirmed the deletion.
   */
  const handleDeleteConfirmationModalActionClick = (isConfirmed: boolean) => {
    if (isConfirmed) {
      setLocationCopy(null);
      setValue(
        "eventLocations",
        getValues("eventLocations").filter((_, i) => i !== index) ?? [],
        { shouldDirty: true, shouldValidate: true },
      );
    }
    setIsDeleteConfirmationModalOpen(false);
  };

  /**
   * Handles the close event for the edit modal.
   * - Closing will revert the location model to the state when the modal was opened
   * - In the case of adding a new other location, closing should discard the row that was added
   */
  const handleModalClose = () => {
    if (modalMode === "add") {
      // Drop exactly the row this modal added. Slicing from `index` instead
      // would strand earlier placeholders when two adds overlap, and could
      // truncate real locations if `index` were ever stale.
      setValue(
        "eventLocations",
        getValues("eventLocations").filter((_, i) => i !== index),
        { shouldDirty: true },
      );
    } else {
      setValue(
        formFieldRoot,
        locationCopy as PathValue<
          EventCreateEditFormValues,
          Path<EventCreateEditFormValues>
        >,
        { shouldDirty: true, shouldValidate: true },
      );
    }
    setIsEditModalOpen(false);
    // We need to trigger the form here to ensure superRefine is aware of the changes
    trigger();
  };

  /**
   * Safety net: the "add" flow writes a placeholder row into the form before
   * the modal opens, so any dismissal path that doesn't run handleModalClose
   * (escape, outside click, an unmount, a programmatic close) would otherwise
   * leave a blank row behind. Whenever the modal finishes closing, drop any
   * row that never got filled in.
   */
  const wasEditModalOpen = useRef(false);
  useEffect(() => {
    if (wasEditModalOpen.current && !isEditModalOpen) {
      const current = getValues("eventLocations") ?? [];
      const cleaned = current.filter(
        location => !isEmptyLocation(location as EventLocationFormValues),
      );
      if (cleaned.length !== current.length) {
        setValue("eventLocations", cleaned, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
    wasEditModalOpen.current = isEditModalOpen;
  }, [isEditModalOpen, getValues, setValue]);

  /**
   * Handles the done event for the edit modal.
   * - Since the modal is attached to the form, we can just close it and the
   *   values will be updated automatically.
   * - We trigger the form to validate the location form values and show errors.
   */
  const handleModalDone = async () => {
    const isValid = await trigger();
    // This gets the errors after the trigger
    // formState won't have the new errors until the next render
    const fieldState = getFieldState(formFieldRoot);

    if (isValid || !fieldState.error) {
      setIsEditModalOpen(false);
    }
  };

  return {
    eventLocations,
    isEditModalOpen,
    isDeleteConfirmationModalOpen,
    index,
    formFieldRoot,
    locationCopy,
    handleAddLocationClick,
    handleEditLocationClick,
    handleDeleteLocationClick,
    handleDeleteConfirmationModalActionClick,
    handleModalClose,
    handleModalDone,
  };
};
